import type { FilterOption, FiltersAPIRawResponse } from './types';
import type { FilterSelection, FiltersResponse } from '@/domain/prices/filters/types';

import type { RoomOccupancy, RoomsParticipantDistribution } from '@/domain/shared/types';
import { sub } from 'date-fns';
import { CHILDREN_MAX_AGE, CHILDREN_MIN_AGE } from '@/domain/shared/constants';
import { mapMonthValue } from '@/domain/prices/utils';

export interface MappedFiltersResponse extends FiltersResponse {
  filterSelection: FilterSelection;
  isError?: boolean;
  isEmpty?: boolean;
}

export interface FiltersResponseMapper {
  map: (
    filtersResponse: FiltersAPIRawResponse,
    originalParticipants?: RoomsParticipantDistribution | undefined
  ) => MappedFiltersResponse;
}

export const createFiltersResponseMapper = (): FiltersResponseMapper => {
  const map = (
    filtersResponse: FiltersAPIRawResponse,
    originalParticipants: RoomsParticipantDistribution | undefined = undefined
  ) => {
    const mappedResponse = {
      DepartureAirport: {
        options:
          filtersResponse.departureAirports?.map(({ text, value, groupInfo, selected }) => ({
            name: text,
            value,
            groupInfo,
            selected,
          })) ?? [],
      },
      Duration: {
        options: filtersResponse.durations?.map(({ text, value, selected }) => ({ name: text, value, selected })) ?? [],
      },
      Mealplan: {
        options: filtersResponse.mealplans?.map(({ text, value, selected }) => ({ name: text, value, selected })) ?? [],
      },
      Month: {
        options:
          filtersResponse.months?.map(({ text, value, selected }) => ({
            name: text,
            value: mapMonthValue(value),
            selected,
          })) ?? [],
      },
      TransportType: {
        options:
          filtersResponse.transportTypes?.map(({ text, value, selected }) => ({ name: text, value, selected })) ?? [],
      },
      RoomType: {
        options:
          filtersResponse.roomTypes?.map(({ text, value, count }) => ({ name: text, value, resultsCount: count })) ??
          [],
      },
      Participants: [],
      filterSelection: {
        DepartureAirport: getSelectedValues(filtersResponse.departureAirports),
        Duration: getSelectedValues(filtersResponse.durations),
        Mealplan: getSelectedValues(filtersResponse.mealplans).join(),
        Month: mapMonthValue(getSelectedValues(filtersResponse.months).join()),
        TransportType: getSelectedValues(filtersResponse.transportTypes).join(),
        RoomType: getSelectedValues(filtersResponse.roomTypes),
        Participants: originalParticipants,
      },
    } as MappedFiltersResponse;

    const isEmpty = checkIsEmptyResponse(mappedResponse);

    return {
      ...mappedResponse,
      isEmpty,
    };
  };

  function getSelectedValues(options: FilterOption[]) {
    return options?.filter((option) => option.selected).map((option) => option.value) ?? [];
  }

  function checkIsEmptyResponse(filters?: Partial<MappedFiltersResponse>) {
    if (!filters) return true;
    const keysToCheck: (keyof MappedFiltersResponse)[] = ['Duration', 'Mealplan', 'Month', 'TransportType'];
    return keysToCheck.every((key) => {
      const filter = filters[key] as { options?: unknown[] };
      return Array.isArray(filter?.options) && filter.options.length === 0;
    });
  }

  return { map };
};
